import { IssueCaseDoc, OrderDoc, OrderStatusCode } from '@gooduncles/gu-app-schema';
import { Tabs } from 'antd';
import { formatDate } from 'date-fns';
import { FC, useEffect, useState } from 'react';

import { observeIssueCases, observeOrders } from 'src/utils/firebase/firebase-short-cut';

import StoreSearch from 'src/atomic-components/molecules/StoreSearch/StoreSearch';
import TabNavigator from 'src/atomic-components/molecules/TabNavigator/TabNavigator';

import classes from './IssueRegister.module.scss';

import IssueTabContent from './IssueTabContent';
import OrderTabContent from './OrderTabContent';

const IssueRegister: FC = () => {
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const [orders, setOrders] = useState<OrderDoc[]>([]);
  const [issueCases, setIssueCases] = useState<IssueCaseDoc[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<string>('최근 주문');

  const handleStoreSelect = (storeId: string | null) => {
    setSelectedStoreId(storeId);
    setSelectedOrderId(null);
    setSelectedIssueId(null);
  };

  useEffect(() => {
    if (!selectedStoreId) {
      setOrders([]);
      return;
    }

    const subscription = observeOrders(
      [
        ['storeId', '==', selectedStoreId],
        ['orderStatus', 'in', [OrderStatusCode.ACCEPTED, OrderStatusCode.DELIVERED]],
      ],
      {
        sortKey: 'date',
        orderBy: 'desc',
        limit: 7,
      }
    ).subscribe((orders) => {
      setOrders(orders);
      if (orders.length > 0 && !selectedOrderId) {
        setSelectedOrderId(orders[0]._id);
      }
    });

    return () => subscription.unsubscribe();
  }, [selectedStoreId, selectedOrderId]);

  useEffect(() => {
    if (!selectedStoreId) {
      setIssueCases([]);
      return;
    }

    const subscription = observeIssueCases([['storeId', '==', selectedStoreId]], {
      sortKey: 'registeredAt',
      orderBy: 'desc',
      limit: 7,
    }).subscribe((cases) => {
      setIssueCases(cases);
      if (cases.length > 0 && !selectedIssueId) {
        setSelectedIssueId(cases[0]._id);
      }
    });

    return () => subscription.unsubscribe();
  }, [selectedStoreId, selectedIssueId]);

  const orderTabs = orders.map((order) => ({
    key: order._id,
    label: formatDate(order.orderDate, 'yy.MM.dd'),
    children: <OrderTabContent order={order} />,
  }));

  const issueTabs = issueCases.map((issueCase) => ({
    key: issueCase._id,
    label: formatDate(new Date(issueCase.registeredAt), 'yy.MM.dd'),
    children: <IssueTabContent issueCase={issueCase} />,
  }));

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.storeSearchWrapper}>
          <StoreSearch selectedStoreId={selectedStoreId} onStoreSelect={handleStoreSelect} />
        </div>
        <TabNavigator options={['최근 주문', '최근 이슈']} value={selectedTab} onChange={setSelectedTab} />
      </div>

      <div className={classes.content}>
        {selectedTab === '최근 주문' ? (
          <div className={classes.tabPanel}>
            {orders.length > 0 ? (
              <Tabs
                size='small'
                activeKey={selectedOrderId || undefined}
                onChange={setSelectedOrderId}
                items={orderTabs}
                style={{ height: '100%', overflow: 'scroll' }}
                tabBarStyle={{ marginBottom: 0 }}
              />
            ) : (
              <div className={classes.emptyState}>매장을 선택하면 주문 목록이 표시됩니다</div>
            )}
          </div>
        ) : (
          <div className={classes.tabPanel}>
            {issueCases.length > 0 ? (
              <Tabs
                size='small'
                activeKey={selectedIssueId || undefined}
                onChange={setSelectedIssueId}
                items={issueTabs}
                style={{ height: '100%', overflow: 'scroll' }}
                tabBarStyle={{ marginBottom: 0 }}
              />
            ) : (
              <div className={classes.emptyState}>매장을 선택하면 이슈 목록이 표시됩니다</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IssueRegister;
